import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";

export interface TagFinderOption {
    /** What is stored: a catalogue code, or the therapist's own words. */
    value: string;
    label: string;
}

/**
 * How many to show before asking the therapist to narrow the search.
 *
 * Long enough to browse a little, short enough that the list does not push the
 * thing being filtered off the screen — which is what a native `<datalist>`
 * did with a 29-entry catalogue.
 */
const VISIBLE_LIMIT = 6;

interface TagFinderProps {
    label: string;
    options: TagFinderOption[];
    value?: string;
    onChange: (next?: string) => void;
    /** Accepts what was typed when nothing matches. Off where the list is closed. */
    allowCustom?: boolean;
    placeholder: string;
}

/**
 * Search-and-pick, for a catalogue too long to be a menu.
 *
 * The same shape as the patient finder the scheduler and payments use: a
 * search field, a handful of matches listed in the flow of the page, and a
 * single row with a cross once something is chosen. A dropdown that overlays
 * the page with thirty rows answers "what exists" and hides "what am I
 * filtering", which is the question the therapist actually has.
 *
 * With `allowCustom` the words themselves are accepted when nothing matches,
 * because a meditation can be filed under a tag the catalogue has no entry
 * for, and a library you can tag something in but not search by is not one.
 */
export default function TagFinder({
    label,
    options,
    value,
    onChange,
    allowCustom = false,
    placeholder,
}: TagFinderProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    // Shut until asked for: a list nobody opened is a list in the way.
    const [open, setOpen] = useState(false);

    const selectedLabel = useMemo(() => {
        if (!value) return null;
        return options.find((option) => option.value === value)?.label ?? value;
    }, [options, value]);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((option) =>
            option.label.toLowerCase().includes(needle)
        );
    }, [options, query]);

    const shown = matches.slice(0, VISIBLE_LIMIT);
    const hidden = matches.length - shown.length;

    const typed = query.trim();
    const custom =
        allowCustom &&
        typed &&
        !options.some((option) => option.label.toLowerCase() === typed.toLowerCase())
            ? typed
            : null;

    const choose = (next: string) => {
        onChange(next);
        setQuery("");
        setOpen(false);
    };

    if (selectedLabel) {
        return (
            <div className="flex flex-col gap-1 text-sm">
                <span className="text-xs font-medium text-content-tertiary">
                    {label}
                </span>
                <div className="flex min-w-0 items-center gap-2 rounded-xl border border-border-subtle px-3 py-2.5">
                    <span className="min-w-0 flex-1 truncate font-medium text-content-primary">
                        {selectedLabel}
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            onChange(undefined);
                            setQuery("");
                        }}
                        aria-label={t("common.clear", "Clear")}
                        className="text-content-tertiary hover:text-content-secondary"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs font-medium text-content-tertiary">{label}</span>

            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-tertiary" />
                <input
                    type="text"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        // Enter takes the single remaining match, or the words
                        // themselves where the catalogue has none.
                        if (matches.length === 1) choose(matches[0].value);
                        else if (custom) choose(custom);
                    }}
                    aria-label={label}
                    placeholder={placeholder}
                    className="field-base field-default h-11 w-full bg-surface-sunken py-2.5 pl-9 pr-3 text-sm"
                />
            </div>

            {open && (
                <div className="flex flex-col gap-1 rounded-xl border border-border-subtle bg-surface-raised p-1">
                    {custom && (
                        <button
                            type="button"
                            onClick={() => choose(custom)}
                            className="rounded-lg px-3 py-2 text-left text-sm text-accent-text hover:bg-surface-sunken"
                        >
                            {t("common.add", "Add")} “{custom}”
                        </button>
                    )}

                    {shown.length === 0 && !custom ? (
                        <p className="px-3 py-2 text-sm text-content-tertiary">
                            {t("publicDirectory.settings.picker.noMatches", { query })}
                        </p>
                    ) : (
                        shown.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => choose(option.value)}
                                className="truncate rounded-lg px-3 py-2 text-left text-sm text-content-secondary hover:bg-surface-sunken hover:text-content-primary"
                            >
                                {option.label}
                            </button>
                        ))
                    )}

                    {hidden > 0 && (
                        <p className="px-3 py-1.5 text-xs text-content-tertiary">
                            {t("meditation.filters.hidden", { count: hidden })}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
